/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useEffect, useState } from "react";
import { Alert, Box, useTheme } from "@mui/material";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import Backdrop from "@mui/material/Backdrop";
import Fade from "@mui/material/Fade";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { StateMapping, STATES_MAPPING } from "../../utils/Constants";
import Grid from "@mui/material/Grid";

import { Company, ModalBase, Nullable, ServiceLocation } from "../../utils/Interfaces";
import { selectExistingCompanies } from "../office-companies/companiesSlice";
import { getStateMapping } from "../../utils/Functions";
import { editSite, editSiteValidation, updateSites } from "./workSitesFunctions";
import { selectExistingSites } from "./workSitesSlice";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";

export interface EditSiteModalProps extends ModalBase {
    site: ServiceLocation;
}

const EditSiteModal = (props: EditSiteModalProps) => {
    const { open, handleClose, site } = props;

    const existingCompanies: Company[] = useAppSelector(selectExistingCompanies);

    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const [storeNo, setStoreNo] = useState<string>("");
    const [streetAddress, setStreetAddress] = useState<string>("");
    const [storeType, setStoreType] = useState<string>("");
    const [city, setCity] = useState<string>("");
    const [state, setState] = useState<StateMapping>(STATES_MAPPING[46]);
    const [company, setCompany] = useState<Company>(existingCompanies[0]);
    const [zipCode, setZipCode] = useState<Nullable<string>>("");
    const [comments, setComments] = useState<Nullable<string>>(null);

    const existingSites: ServiceLocation[] = useAppSelector(selectExistingSites);

    const [validationErrorMessage, setValidationErrorMessage] = useState<string>("");

    const theme = useTheme();

    function modifiedSite(key: keyof ServiceLocation, value: string | number | Company | StateMapping): ServiceLocation {
        let siteTemplate: ServiceLocation = {
            id: site.id,
            store_no: storeNo,
            street_address: streetAddress,
            store_type: storeType,
            city: city,
            state: state.abbrev,
            company: company,
            zip_code: zipCode ? parseInt(zipCode) : null,
            comments: comments,
        };

        if ((value as StateMapping).abbrev !== undefined) {
            // @ts-ignore
            siteTemplate[key] = (value as StateMapping).abbrev;
        } else {
            // @ts-ignore
            siteTemplate[key] = value;
        }

        return siteTemplate;
    }

    useEffect(() => {
        setStoreNo(site.store_no);
        setStreetAddress(site.street_address);
        setCity(site.city);
        setState(getStateMapping(site.state));
        setCompany(site.company);
        setZipCode(site.zip_code ? site.zip_code.toString() : null);
        setComments(site.comments);
        setStoreType(site.store_type);

        setValidationErrorMessage(editSiteValidation(site, existingSites, site.store_no));
    }, [existingCompanies, existingSites, dispatch, site]);

    return (
        <Box>
            <Modal
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
                open={open}
                onClose={handleClose}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={open}>
                    <Box
                        sx={{
                            backgroundColor: theme.palette.background.paper,
                            border: "1px solid #000",
                            boxShadow: theme.shadows[5],
                            padding: theme.spacing(2, 4, 3),
                            zIndex: 1000,
                        }}
                    >
                        <Alert
                            severity={validationErrorMessage === "" ? "success" : "error"}
                            sx={{
                                mb: 2,
                            }}
                        >
                            {validationErrorMessage === "" ? "All changes are valid." : validationErrorMessage}
                        </Alert>

                        <Box component="form">
                            <TextField
                                onChange={(event) => {
                                    setStoreNo(event.target.value);
                                    setValidationErrorMessage(
                                        editSiteValidation(
                                            modifiedSite("store_no", event.target.value),
                                            existingSites,
                                            site.store_no
                                        )
                                    );
                                }}
                                margin="normal"
                                required
                                fullWidth
                                id="store_no"
                                label="Store Number"
                                name="store_no"
                                value={storeNo}
                            />
                            <TextField
                                onChange={(event) => {
                                    setStreetAddress(event.target.value);
                                    setValidationErrorMessage(
                                        editSiteValidation(
                                            modifiedSite("street_address", event.target.value),
                                            existingSites,
                                            site.store_no
                                        )
                                    );
                                }}
                                margin="normal"
                                required
                                fullWidth
                                id="street_address"
                                label="Street Address"
                                name="street_address"
                                value={streetAddress}
                            />
                            <TextField
                                onChange={(event) => {
                                    setCity(event.target.value);
                                    setValidationErrorMessage(
                                        editSiteValidation(modifiedSite("city", event.target.value), existingSites, site.store_no)
                                    );
                                }}
                                margin="normal"
                                required
                                fullWidth
                                id="city"
                                label="City"
                                name="city"
                                value={city}
                            />

                            <Grid container spacing={3}>
                                <Grid item xs={6}>
                                    <Autocomplete
                                        disablePortal
                                        onChange={(_event, value) => {
                                            setState(value ? value : STATES_MAPPING[46]);
                                            setValidationErrorMessage(
                                                editSiteValidation(
                                                    modifiedSite("state", value ? value : STATES_MAPPING[46]),
                                                    existingSites,
                                                    site.store_no
                                                )
                                            );
                                        }}
                                        sx={{ mt: 2 }}
                                        id="state"
                                        getOptionKey={(option: StateMapping) => option.abbrev}
                                        options={STATES_MAPPING}
                                        value={state}
                                        getOptionLabel={(option: StateMapping) => option.name}
                                        renderInput={(params) => <TextField required {...params} label="State" />}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        onChange={(event) => {
                                            setZipCode(event.target.value);
                                            setValidationErrorMessage(
                                                editSiteValidation(
                                                    modifiedSite("zip_code", event.target.value),
                                                    existingSites,
                                                    site.store_no
                                                )
                                            );
                                        }}
                                        margin="normal"
                                        required
                                        fullWidth
                                        id="zip_code"
                                        label="Zip Code"
                                        name="zip_code"
                                        value={zipCode}
                                    />
                                </Grid>
                            </Grid>

                            <Grid container spacing={3}>
                                <Grid item xs={6}>
                                    <Autocomplete
                                        disablePortal
                                        onChange={(_event, value) => {
                                            setCompany(value ? value : existingCompanies[0]);
                                            setValidationErrorMessage(
                                                editSiteValidation(
                                                    modifiedSite("company", value ? value : existingCompanies[0]),
                                                    existingSites,
                                                    site.store_no
                                                )
                                            );
                                        }}
                                        sx={{ mt: 2 }}
                                        id="company"
                                        getOptionKey={(option: Company) => option.id}
                                        options={existingCompanies}
                                        value={company}
                                        getOptionLabel={(option: Company) => option.name}
                                        renderInput={(params) => <TextField required {...params} label="Company" />}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        onChange={(event) => {
                                            setStoreType(event.target.value);
                                            setValidationErrorMessage(
                                                editSiteValidation(
                                                    modifiedSite("store_type", event.target.value),
                                                    existingSites,
                                                    site.store_no
                                                )
                                            );
                                        }}
                                        margin="normal"
                                        required
                                        fullWidth
                                        id="store_type"
                                        label="Store Type"
                                        name="store_type"
                                        value={storeType}
                                    />
                                </Grid>
                            </Grid>

                            <TextField
                                onChange={(event) => {
                                    setComments(event.target.value);
                                    setValidationErrorMessage(
                                        editSiteValidation(
                                            modifiedSite("comments", event.target.value),
                                            existingSites,
                                            site.store_no
                                        )
                                    );
                                }}
                                margin="normal"
                                fullWidth
                                multiline
                                rows={4}
                                id="comments"
                                label="Site Comments"
                                name="comments"
                                value={comments === null || comments === undefined ? "" : comments}
                            />

                            <Button
                                onClick={() => {
                                    editSite(modifiedSite("id", site.id), dispatch, () => {
                                        updateSites(dispatch, () => {});
                                    });

                                    handleClose();
                                }}
                                fullWidth
                                variant="contained"
                                sx={{ mt: 3, mb: 2 }}
                                color="primary"
                                disabled={validationErrorMessage !== ""}
                            >
                                Submit
                            </Button>
                        </Box>
                    </Box>
                </Fade>
            </Modal>
        </Box>
    );
};

export default EditSiteModal;
